/**
 * Auth Interceptor
 *
 * Attaches an Azure AD bearer token (acquired via CalAngularService) to every
 * outgoing HttpClient request that targets this app's API (environment.apiEndpoint).
 * Requests to other origins (e.g. Microsoft Graph, which acquires its own token
 * inline) are left untouched.
 */

import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor
} from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { CalAngularService } from '@cvx/cal-angular';
import { firstValueFrom } from 'rxjs';
import { environment } from '@env/environment';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(private calService: CalAngularService) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Only attach the token for calls made to our own API.
    if (!environment.apiEndpoint || !request.url.startsWith(environment.apiEndpoint)) {
      return next.handle(request);
    }

    return from(this.getToken()).pipe(
      switchMap(token => {
        const authorizedRequest = token
          ? request.clone({
              setHeaders: { Authorization: `Bearer ${token}` }
            })
          : request;
        return next.handle(authorizedRequest);
      })
    );
  }

  private async getToken(): Promise<string | null> {
    try {
      const scopes = (environment as any).scopes as string[] | undefined;
      const result: any = await firstValueFrom(this.calService.getAADToken(scopes));
      if (!result) {
        return null;
      }
      // getAADToken may resolve to a raw string token or an AuthenticationResult object,
      // depending on the @cvx/cal-angular version — support both shapes.
      return typeof result === 'string' ? result : (result.accessToken ?? null);
    } catch (error) {
      console.error('Failed to acquire Azure AD token for API request:', error);
      return null;
    }
  }
}
